import { Button, Checkbox, Divider, Form, Modal, Segment } from "semantic-ui-react";
import Calendar, { datePlusDays } from "./Calendar"
import { DateInput, DateTimeInput } from "semantic-ui-calendar-react";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useRouteMatch } from "react-router-dom";
import api from "api";
import { OpenAPI } from "simplydo/interfaces";
import toast from "react-hot-toast";
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";
import { ColourChooser } from "components/lib/Choosers";
import styled from "styled-components";
import moment from "moment";
import RichText from "components/lib/Editors/RichText";
import { useAppSelector } from "store";
import util from "utils/utils";

type CalendarResp = OpenAPI.GET<'/challenges/{id}/calendar'>["response"];

const ColorPickerField = styled(Form.Field)`
  .pcr-button {
    width: 38px;
    height: 38px;
  }`;

const Planner = ({ challenge }) => {
  const [calendar, setCalendar] = useState<CalendarResp['calendar']>(null);
  const [isLoading, setLoading] = useState(true);
  const [creatingEvent, setCreatingEvent] = useState(null);
  const match = useRouteMatch();

  const user = useAppSelector((state) => state.user);
  const canManageChallenge = util.canManageChallenge(user, challenge);

  // @ts-ignore
  const challengeId = match.params.id;

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    api.challenges.getCalendar(challengeId, ({ calendar }: CalendarResp) => {
      if (isMounted) {
        setCalendar(calendar);
        setLoading(false);
      }
    }, (error) => {
      toast.error(error);
    });
    return () => { isMounted = false };
  }, [challengeId]);

  const from = calendar?.start ?? new Date().toISOString();

  const nextMonth = new Date(from);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const to = calendar?.end ?? nextMonth.toISOString();

  // Calculate min and max dates for the date pickers
  const toMinDate = datePlusDays(from, 1);
  const fromMaxDate = datePlusDays(to, -1);

  const setFrom = useCallback((value: string) => {
    api.challenges.updateCalendar(challengeId, { start: value }, () => {
      setCalendar((prev) => ({ ...prev, start: value }));
    }, (error) => {
      toast.error(error.message);
    });
  }, [challengeId]);

  const setTo = useCallback((value: string) => {
    api.challenges.updateCalendar(challengeId, { end: value }, () => {
      setCalendar((prev) => ({ ...prev, end: value }));
    }, (error) => {
      toast.error(error.message);
    });
  }, [challengeId]);

  const createEvent = useCallback(() => {
    api.challenges.createCalendarEvent(challengeId, creatingEvent, ({ events }) => {
      setCreatingEvent(null);
      setCalendar((prev) => ({ ...prev, events }));
    }, (error) => {
      toast.error(error.message);
    });
  }, [challengeId, creatingEvent]);

  const updateEventField = useCallback((field, value) => {
    setCreatingEvent((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateRichtext = useCallback((value, _text, fieldName) => {
    updateEventField(fieldName, value);
  }, [updateEventField]);

  const deleteEvent = useCallback((eventId) => {
    util.confirm("Delete event", "Are you sure you want to delete this event?")
      .then(() => {
        api.challenges.deleteCalendarEvent(challengeId, eventId, ({ events }) => {
          setCalendar((prev) => ({ ...prev, events }));
        }, (error) => {
          toast.error(error.message);
        });
      })
      .catch(() => { });
  }, [challengeId]);

  return (
    <div>
      <Modal open={creatingEvent !== null}>
        <Modal.Header>Create a new event</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group>
              <Form.Field>
                <label>Name:</label>
                <input
                  placeholder="Event name"
                  value={creatingEvent?.title}
                  onChange={(e) => setCreatingEvent((prev) => ({ ...prev, title: e.target.value }))}
                />
              </Form.Field>
              <ColorPickerField>
                <label>Color:</label>
                <ColourChooser
                  value={creatingEvent?.color}
                  onChange={(color) => setCreatingEvent((prev) => ({ ...prev, color }))}
                />
              </ColorPickerField>
            </Form.Group>
            <Form.Field>
              <label>Description:</label>
              <RichText
                value={creatingEvent?.description || ''}
                emptyReturnValue={''}
                placeholder='Describe this event...'
                onChange={updateRichtext}
                extraData='description'
                forType='challenge'
                forId={challengeId}
              />

            </Form.Field>
            <Checkbox
              toggle
              label="Unspecified end"
              checked={creatingEvent?.noTo}
              onChange={(e, { checked }) => setCreatingEvent((prev) => ({ ...prev, noTo: checked }))}
            />
            <Form.Group>
              <Form.Field>
                <label>Start:</label>
                <DateTimeInput
                  disableMinute={true}
                  dateTimeFormat="DD-MM-YYYY HH:mm"
                  value={creatingEvent?.from ? moment(creatingEvent?.from).format('DD-MM-YYYY HH:mm') : null}
                  onChange={(e, { value }) => setCreatingEvent((prev) => ({ ...prev, from: moment(value, 'DD-MM-YYYY HH:mm').toISOString() }))}
                />
              </Form.Field>

              {!creatingEvent?.noTo ? (
                <Form.Field>
                  <label>End:</label>
                  <DateTimeInput
                    disableMinute={true}
                    dateTimeFormat="DD-MM-YYYY HH:mm"
                    value={creatingEvent?.to ? moment(creatingEvent?.to).format('DD-MM-YYYY HH:mm') : null}
                    onChange={(e, { value }) => {
                      setCreatingEvent((prev) => ({ ...prev, to: moment(value, 'DD-MM-YYYY HH:mm').toISOString() }))
                    }}
                  />
                </Form.Field>
              ) : null}
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCreatingEvent(null)}>Cancel</Button>
          <Button
            primary
            onClick={() => createEvent()}
            disabled={!creatingEvent?.title || !creatingEvent?.from || !creatingEvent?.color || (!creatingEvent?.noTo && !creatingEvent?.to)}
          >Create</Button>
        </Modal.Actions>
      </Modal>
      <h1>Planner</h1>

      <Segment>
        {isLoading ? (
          <PlaceholderStack gap={25}>
            <PlaceholderItem height={50} />
            <PlaceholderItem height={400} />
          </PlaceholderStack>
        ) : (
          <>
            {canManageChallenge ? (
              <Form>
                <Form.Group>
                  <Form.Field>
                    <label>Running from:</label>
                    <DateInput
                      value={from.split("T")[0]}
                      dateFormat="YYYY-MM-DD"
                      onChange={(e, { value }) => setFrom(value)}
                      maxDate={fromMaxDate.toISOString().split("T")[0]}
                    />
                  </Form.Field>

                  <Form.Field>
                    <label>to:</label>
                    <DateInput
                      value={to.split("T")[0]}
                      dateFormat="YYYY-MM-DD"
                      onChange={(e, { value }) => setTo(value)}
                      minDate={toMinDate.toISOString().split("T")[0]}
                    />
                  </Form.Field>
                </Form.Group>
                <Form.Group>
                  <Form.Field>
                    <Form.Button onClick={() => setCreatingEvent({
                      id: uuidv4(),
                      color: '#2185d0',
                    })}>
                      Add Event
                    </Form.Button>
                  </Form.Field>
                </Form.Group>
              </Form>
            ) : null}

            <Divider />

            <Calendar
              events={calendar?.events ?? []}
              start={from}
              end={to}
              deleteEvent={canManageChallenge ? deleteEvent : null}
            />
          </>
        )}
      </Segment>
    </div>
  );
};

export default Planner;
